import React, {useState} from "react";
import {PercentIcon} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient} from '@tanstack/react-query';
import {Modal, ModalFooter, FormField, SwitchRow, inputClass} from "./SettingsUI";

const TaxEditor = ({tax, onClose, showNotification}) => {
    const [formData, setFormData] = useState({
        name: tax ? tax.name : '',
        value: tax ? tax.value : '',
        active: tax ? tax.active : true
    });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const queryClient = useQueryClient();

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Le nom de la taxe est requis";
        }

        if (!formData.value && formData.value !== 0) {
            newErrors.value = "Le taux de la taxe est requis";
        } else if (isNaN(parseFloat(formData.value)) || parseFloat(formData.value) < 0) {
            newErrors.value = "Le taux doit être un nombre positif";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setSubmitting(true);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Prepare request data
            const payload = {
                name: formData.name,
                value: formData.value,
                active: formData.active
            };

            let response;

            if (tax) {
                // Update existing tax
                response = await api.put(`/taxes/${tax.id}`, payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Taxe mise à jour avec succès');
            } else {
                // Create new tax
                response = await api.post('/taxes', payload, {
                    headers: {
                        'X-account-id': accountId.id,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                showNotification('success', 'Taxe créée avec succès');
            }

            // Invalidate queries to refetch data
            queryClient.invalidateQueries({queryKey: ['taxes']});
            onClose();
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur s'est produite";
            showNotification('error', `Erreur: ${errorMessage}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={tax ? 'Modifier la taxe' : 'Nouvelle taxe'}
            onClose={onClose}
            footer={<ModalFooter onCancel={onClose} form="tax-form" saving={submitting}
                                 label={tax ? 'Enregistrer' : 'Créer'}/>}
        >
            <form id="tax-form" onSubmit={handleSubmit} className="space-y-4">
                <FormField id="name" label="Nom de la taxe" required error={errors.name}>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={inputClass(errors.name)}
                        placeholder="ex: TVA Standard"
                    />
                </FormField>

                <FormField id="value" label="Taux (%)" required error={errors.value}>
                    <div className="relative">
                        <input
                            type="text"
                            inputMode="decimal"
                            id="value"
                            name="value"
                            value={formData.value}
                            onChange={handleChange}
                            className={`${inputClass(errors.value)} pr-10`}
                            placeholder="ex: 20.00"
                        />
                        <PercentIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"/>
                    </div>
                </FormField>

                <SwitchRow id="active" label="Actif" checked={formData.active}
                           onChange={(checked) => setFormData(prev => ({...prev, active: checked}))}/>
            </form>
        </Modal>
    );
};

export default TaxEditor;
